// Real Web Push: a subscription created here is registered with the
// backend (save_push_subscription.php) and can receive notifications
// pushed by the server (push_helper.php) even when this app is fully
// closed -- unlike the old localStorage-diff/foreground-poll approach,
// this needs no open tab to fire.
import { authFetch, API_BASE } from './api';
import { VAPID_PUBLIC_KEY } from '../config/vapid';

export function notificationPermissionState() {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

export function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return Promise.resolve('unsupported');
  return Notification.requestPermission();
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * Creates (or reuses) a PushSubscription for this device and registers it
 * with the backend. Safe to call repeatedly -- idempotent both in the
 * browser (pushManager.subscribe() returns the existing subscription if
 * one's already active) and on the server (ON DUPLICATE KEY UPDATE).
 *
 * Returns { ok, error } instead of a plain boolean so callers with a UI
 * (e.g. the Home page's "Enable" button) can show the real failure reason
 * -- important on mobile, where there's no console to check.
 */
export async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, error: 'Push not supported on this browser' };
  }
  if (Notification.permission !== 'granted') {
    return { ok: false, error: 'Notification permission not granted' };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const res = await authFetch(`${API_BASE}/save_push_subscription.php`, {
      method: 'POST',
      body: JSON.stringify(subscription.toJSON()),
    });
    if (!res.ok) {
      const bodyText = await res.text();
      console.error('Push subscription save failed:', res.status, bodyText);
      let message = bodyText;
      try { message = JSON.parse(bodyText).error || bodyText; } catch { /* not JSON */ }
      return { ok: false, error: `Server error (${res.status}): ${message}`.slice(0, 200) };
    }
    return { ok: true };
  } catch (err) {
    console.error('Push subscription failed:', err);
    return { ok: false, error: err.message || String(err) };
  }
}
